import { logger } from '@/utils/logger'

/**
 * A module for parsing the raw payloads of JWTs issued by SWS Identity service and
 * exposing their header and payload data.
 *
 * Note: This module does NOT in any way validate the JWTs. It simply exposes their
 * data for the purposes of display and/or driving UI logic.
 *
 * Only SWS web services can validate JWTs issued by the SWS Identity Service.
 */

export interface TokenHeader {
  alg: string
  crit: string[]
  aid: string
  kid: string
  kct: string
}

interface Token {
  header: TokenHeader
  iss: string
  aud: string[]
  sub: string
  iat: Date
  exp: Date
  uid: string
}

export interface RefreshToken extends Token {
  uuid: string
}

export interface AccessToken extends Token {
  app_id: string
  app_name: string
  scopes: AccessScopes
  rtid: string
}

export type AccessScopes = Map<string, Array<string>>

export enum SwsService {
  License = 'license.serato.io',
  Identity = 'id.serato.io',
  Ecom = 'ecom.serato.com',
  Profile = 'profile.serato.com',
  Notifcations = 'notifications.serato.com',
  DigitalAssets = 'da.serato.com'
}

export function parseToken<T extends AccessToken | RefreshToken> (raw: string): T {
  const bits = raw.split('.')
  return Object.assign(
    { header: parseRaw(bits[0]) },
    parseRaw(bits[1]) ?? {}
  ) as T
}

// ignore linter check here because JSON.parse() function in the module is defined to return any
// eslint-disable-next-line
const parseRaw = (raw: string): any => {
  if (atob(raw)) {
    const serviceNames: Array<string> = Object.values(SwsService)
    const scopes = new Map()
    // ignore linter check here because JSON.parse() function in the module is defined to have a 2nd parameter 
    // `reviver` as any
    // eslint-disable-next-line 
    return JSON.parse(atob(raw), (key: string, value: any): any => {
      if (key === 'iat' || key === 'exp') {
        return new Date(value * 1000)
      } else if (key === 'scopes') {
        return scopes
      } else if (serviceNames.includes(key)) {
        scopes.set(key, value)
      } else {
        return value
      }
    })
  }
}
export interface AccessTokenParsedPayload {
  email: string
  uid: number
}

/**
 * Parses a JWT (JSON Web Token) string and extracts the payload as a JavaScript object.
 *
 * @param {string} token - JWT token
 * @returns {Record<string, unknown> | null} The parsed payload object if successful, or `null` if the token is invalid or the payload cannot be parsed.
 *
 */
export function parseJwtToken (token: string): AccessTokenParsedPayload | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) {
      logger.warn('Invalid JWT format', { operation: 'parse_jwt' })
      return null
    }

    const base64Url = parts[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')

    const jsonPayload: string = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )

    return JSON.parse(jsonPayload)
  } catch (error) {
    logger.error(error instanceof Error ? error : new Error(String(error)), {
      message: 'Error decoding JWT:',
      operation: 'parse_jwt'
    })
    return null
  }
}
